import { inject, Injectable } from '@angular/core';
import {
  BehaviorSubject,
  distinctUntilChanged,
  map,
  Observable,
  of,
  catchError,
  Subject,
  switchMap,
  tap,
} from 'rxjs';
import { LoggedInUser } from 'src/app/shared/store/user/user.model';
import {
  EMPTY_PASSPORT_AUTH_DATA,
  LoginData,
  LoginState,
  PassportAuthData,
} from './passport-auth.types';
import { environment } from 'src/environment/environment';
import { ApiService } from 'src/app/shared/services/api/api.service';
import { Store } from '@ngrx/store';
import { UserActions } from 'src/app/shared/store/user/user.actions';

@Injectable({
  providedIn: 'root',
})
export class PassportAuth {
  private apiService = inject(ApiService);
  private store = inject(Store);

  private authData = new BehaviorSubject<PassportAuthData>(
    PassportAuthData.loadPassportAuthDataFromStorage(),
  );

  private loginDataSubj = new BehaviorSubject<LoginData>(
    this.authDataToInitialLoginData(this.authData.value),
  );

  private codeSink = new Subject<{
    partner: string;
    code: string;
  }>();

  private codeToLoginData(
    code$: Observable<{
      partner: string;
      code: string;
    }>,
  ): Observable<LoginData> {
    return code$.pipe(
      distinctUntilChanged(),
      switchMap(({ code, partner }) =>
        code
          ? this.apiService.getTokenByCode(code).pipe(
              map(token => this.tokenToLoginData(token, partner)),
              catchError(error =>
                of({ state: LoginState.error as const, error, partner }),
              ),
            )
          : of(),
      ),
    );
  }

  private tokenToLoginData(token: string, partner: string): LoginData {
    const user = this.tokenToUser(token);
    if (!user) {
      return {
        state: LoginState.error as const,
        error: { message: 'Invalid token' },
        partner,
      };
    }
    return {
      state: LoginState.loggedIn as const,
      user,
      token,
      partner,
    };
  }

  private loginDataLoadedFromLocalStorage = false;

  private authDataToInitialLoginData(authData: PassportAuthData): LoginData {
    const loggedOut: LoginData = {
      state: LoginState.loggedOut,
      partner: null,
    };
    const partner = authData.currentPartner;
    if (!partner) return loggedOut;
    const tokenData = authData.tokens[partner];
    if (!tokenData) return loggedOut;
    if (tokenData.expiresAt * 1000 < Date.now()) return loggedOut;
    const loginData = this.tokenToLoginData(tokenData.token, partner);
    if (loginData.state === LoginState.loggedIn) {
      this.loginDataLoadedFromLocalStorage = true;
      localStorage.removeItem('returnUrl');
    }
    return loginData;
  }

  // Whenever a new code is availabe, update login data
  private listenToCodeChanges() {
    this.codeToLoginData(this.codeSink).subscribe(loginData =>
      this.loginDataSubj.next(loginData),
    );
  }

  setLoggedOutState() {
    const loggedOut: LoginData = {
      state: LoginState.loggedOut,
      partner: null,
    };
    this.loginDataSubj.next(loggedOut);
  }

  // Whenever auth data changes, write the value to local storage
  private storeAuthData() {
    this.authData.subscribe(PassportAuthData.savePassportAuthDataToStorage);
  }

  // Whenever login data is updated, update auth data
  private updateAuthData() {
    this.loginDataSubj.subscribe(loginData => {
      if (loginData.state === LoginState.loggedIn) {
        this.authData.next(
          PassportAuthData.updatePassportAuthToken(
            this.authData.value,
            loginData.partner,
            { token: loginData.token, expiresAt: loginData.user.expiresAt },
            Date.now(),
          ),
        );
      } else {
        //Todo: revisit to see how this can be set (perhaps a new state can be added when in process of logging in)
        //this.authData.next(EMPTY_PASSPORT_AUTH_DATA);
      }
    });
  }

  constructor() {
    this.listenToCodeChanges();
    this.storeAuthData();
    this.updateAuthData();
    this.updateStore();
  }

  setCurrentPartner(partner: string | null) {
    this.authData.next(
      PassportAuthData.updateCurrentPartner(this.authData.value, partner),
    );
  }

  authenticateWithCode(code: string) {
    const partner = this.authData.value.currentPartner;
    if (partner) {
      this.codeSink.next({ partner, code });
    }
  }

  // method to be implemented
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private scheduleRefresh(partner: string, token: string, expiresAt: number) {
    // TODO
  }

  // A library can be used instead
  private parseJwt(jwtToken: string): unknown {
    const base64Url = jwtToken.split('.')[1];
    if (!base64Url) {
      return undefined;
    }
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const atobBase64 = window.atob(base64);
    const atobBase64Length = atobBase64.length;
    const urlEncodedChars: string[] = [];
    for (let i = 0; i < atobBase64Length; ++i) {
      urlEncodedChars.push(
        `%${atobBase64.charCodeAt(i).toString(16).padStart(2, '0')}`,
      );
    }
    const urlEncoded = urlEncodedChars.join('');
    const jsonPayload = decodeURIComponent(urlEncoded);
    return JSON.parse(jsonPayload);
  }

  private tokenToUser(token: string): LoggedInUser | null {
    let jwtToken: string;
    let parsedToken: unknown;
    try {
      const extractedJwtToken = JSON.parse(window.atob(token)).token;
      if (typeof extractedJwtToken === 'string') {
        jwtToken = extractedJwtToken;
      } else {
        return null;
      }
      parsedToken = this.parseJwt(jwtToken);
    } catch (error) {
      console.error('Error parsing token', token, error);
      return null;
    }
    if (!parsedToken || typeof parsedToken !== 'object') return null;
    if (!('exp' in parsedToken) || typeof parsedToken.exp !== 'number') {
      return null;
    }
    const expiresAt = parsedToken.exp;
    let userName = 'User';
    if (
      'displayName' in parsedToken &&
      typeof parsedToken.displayName === 'string'
    ) {
      userName = parsedToken.displayName;
    }
    let email = '';
    if ('email' in parsedToken && typeof parsedToken.email === 'string') {
      email = parsedToken.email;
    }
    let roles: string[] = [];
    const clientId = environment.comcastClientId;

    if ('application' in parsedToken) {
      const app = parsedToken.application;
      if (app && typeof app === 'object' && !Array.isArray(app)) {
        // Make it indexable for TS:
        const map = app as Record<string, unknown>;
        const v = map[clientId];
        if (Array.isArray(v)) {
          roles = v
            .filter(
              (item): item is { role: string } =>
                item &&
                typeof item === 'object' &&
                'role' in item &&
                typeof (item as { role?: unknown }).role === 'string',
            )
            .map(item => item.role);
        }
      }
    }
    const user: LoggedInUser = {
      userName,
      email,
      roles,
      jwtToken: token,
      // jwtToken,
      expiresAt,
    };
    return user;
  }

  private updateStore() {
    this.loginDataSubj
      .pipe(
        tap(loginData => {
          if (loginData.state === LoginState.loggedIn) {
            this.store.dispatch(
              UserActions.setUser({
                user: loginData.user,
                goToReturnUrl: !this.loginDataLoadedFromLocalStorage,
              }),
            );
          } else if (loginData.state === LoginState.error) {
            this.store.dispatch(UserActions.authError(loginData));
          }
        }),
      )
      .subscribe();
  }
}
